import axios from '../api/api'

/**
 * Submit a comprehensive review for an order including vendor, delivery, and product reviews
 * @param {Object} reviewData - The review data object matching backend expected format
 * @param {string} reviewData.order - Order UUID
 * @param {number} reviewData.vendor_rating - Vendor rating (1-5)
 * @param {string} reviewData.vendor_comment - Vendor review comment
 * @param {number} reviewData.delivery_rating - Overall delivery rating (1-5)
 * @param {string} reviewData.delivery_comment - Delivery review comment
 * @param {Array} reviewData.product_reviews - Array of product review objects
 * @param {Object} reviewData.delivery_review_details - Detailed delivery ratings
 * @returns {Promise<Object>} API response data
 */
export const submitOrderReview = async reviewData => {
  try {
    const response = await axios.post('/orders/reviews/', reviewData)
    return response.data
  } catch (error) {
    console.error('Error submitting review:', error)
    throw error
  }
}

/**
 * Get reviews for a specific order (check if already reviewed)
 * @param {string} orderId - Order UUID
 * @returns {Promise<Object>} Review data if exists
 */
// NOTE: Removed order-specific review fetchers — we only use the prompt endpoints.

/**
 * Get all reviews by the current user
 * @returns {Promise<Array>} Array of user's reviews
 */
export const getUserReviews = async () => {
  try {
    const response = await axios.get('/orders/reviews/user/')
    return response.data
  } catch (error) {
    console.error('Error fetching user reviews:', error)
    throw error
  }
}

/**
 * Get review prompt for the current user (if any recent order requires review)
 * Endpoint: GET /orders/reviews/prompt/
 * @returns {Promise<Object|null>} Prompt payload or null when none
 */
export const getReviewPrompt = async () => {
  try {
    const response = await axios.get('/orders/reviews/prompt/')
    return response.data
  } catch (error) {
    if (error.response?.status === 404) {
      return null
    }
    console.error('Error fetching review prompt:', error)
    throw error
  }
}

/**
 * Dismiss a review prompt for a specific order
 * Endpoint: POST /orders/reviews/prompt_dismiss/
 * @param {string} orderId - Order UUID to dismiss
 * @returns {Promise<Object>} API response
 */
export const dismissReviewPrompt = async orderId => {
  try {
    const payload = { order_id: orderId }
    const response = await axios.post('/orders/reviews/prompt_dismiss/', payload)
    return response.data
  } catch (error) {
    console.error('Error dismissing review prompt:', error)
    throw error
  }
}
